import { trackCalculatorComplete } from "@/components/analytics/conversions";
import { CalculatorArticle } from "@/components/calculators/calculator-article";
import { calculatorArticleHead } from "@/components/calculators/calculator-article-head";
import { calorieGuide } from "@/components/calculators/guides/calorie-guide";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import { Activity, Calculator, Heart, Info, Target } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/calori-calculator")({
  component: CalorieCalculator,
  head: () => ({
    meta: seo({
      title: "Kalori ve TDEE Hesaplayıcı Rehberi | AtalayTheCoach",
      description:
        "Mifflin–St Jeor denklemiyle BMR ve TDEE tahmini hesapla; kilo verme, koruma veya alma hedefini gerçek ağırlık eğiliminle ayarla.",
      keywords:
        "kalori hesaplama, TDEE hesaplayıcı, günlük kalori ihtiyacı, bazal metabolizma, beslenme planı, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/calori-calculator",
    }),
    ...calculatorArticleHead({
      canonical: "https://atalaythecoach.com/essay/calori-calculator",
      headline:
        "Kalori ve TDEE Hesaplayıcı: Sonucu Kişisel Verinle Ayarlama Rehberi",
      description:
        "BMR, TDEE, aktivite katsayısı ve hedef kaloriyi gerçek ağırlık eğilimiyle kalibre etme rehberi.",
    }),
  }),
});

// Aktivite seviyeleri
const activityLevels = {
  1.2: "Hareketsiz (masa başı iş, hiç spor yapmıyor)",
  1.375: "Az aktif (hafif egzersiz 1-3 gün/hafta)",
  1.55: "Orta aktif (orta düzey egzersiz 3-5 gün/hafta)",
  1.725: "Çok aktif (yoğun egzersiz 6-7 gün/hafta)",
  1.9: "Süper aktif (çok yoğun egzersiz, fiziksel iş)",
};

// Hedef türleri
const goals = {
  maintain: {
    name: "Kilo Koruma",
    multiplier: 1,
    description: "Mevcut kilonuzu koruyun",
  },
  lose: {
    name: "Kilo Verme",
    multiplier: 0.8,
    description: "Sağlıklı kilo kaybı (haftalık 0.5kg)",
  },
  gain: {
    name: "Kilo Alma",
    multiplier: 1.15,
    description: "Kas kütlesi artırma (haftalık 0.25kg)",
  },
};

// Makro dağılım tercihleri
const macroSplits = {
  balanced: {
    name: "Dengeli Dağılım (Örnek)",
    detail: "%40 Karb / %30 Protein / %30 Yağ",
    protein: 0.3,
    carbs: 0.4,
    fat: 0.3,
    description: "Karbonhidrat, protein ve yağı dengeli dağıtan örnek seçenek.",
  },
  performance: {
    name: "Performans & Güç",
    detail: "%50 Karb / %30 Protein / %20 Yağ",
    protein: 0.3,
    carbs: 0.5,
    fat: 0.2,
    description: "Karbonhidrat payı daha yüksek örnek performans dağılımı.",
  },
  lowcarb: {
    name: "Düşük Karbonhidrat",
    detail: "%25 Karb / %35 Protein / %40 Yağ",
    protein: 0.35,
    carbs: 0.25,
    fat: 0.4,
    description: "Kilo kontrolü veya karbonhidratı azaltmak isteyenler için.",
  },
};

function CalorieCalculator() {
  const [age, setAge] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [activity, setActivity] = useState<string>("");
  const [goal, setGoal] = useState<string>("maintain");
  const [macroSplit, setMacroSplit] = useState<string>("balanced");
  const [results, setResults] = useState<{
    bmr: number;
    tdee: number;
    goalCalories: number;
    macros: {
      protein: number;
      carbs: number;
      fat: number;
      proteinPercent: number;
      carbsPercent: number;
      fatPercent: number;
    };
  } | null>(null);

  const calculateCalories = () => {
    const ageNum = Number.parseInt(age);
    const weightNum = Number.parseFloat(weight);
    const heightNum = Number.parseFloat(height);
    const activityNum = Number.parseFloat(activity);

    if (!ageNum || !weightNum || !heightNum || !activityNum || !gender) return;

    // BMR hesaplama (Mifflin-St Jeor equation)
    let bmr: number;
    if (gender === "male") {
      bmr = 10 * weightNum + 6.25 * heightNum - 5 * ageNum + 5;
    } else {
      bmr = 10 * weightNum + 6.25 * heightNum - 5 * ageNum - 161;
    }

    // TDEE hesaplama
    const tdee = bmr * activityNum;

    // Hedef kalori
    const goalCalories = tdee * goals[goal as keyof typeof goals].multiplier;

    // Seçilen makro dağılımına göre hesaplama
    const split = macroSplits[macroSplit as keyof typeof macroSplits];
    const macros = {
      protein: Math.round((goalCalories * split.protein) / 4), // 1g protein = 4 kalori
      carbs: Math.round((goalCalories * split.carbs) / 4), // 1g karbonhidrat = 4 kalori
      fat: Math.round((goalCalories * split.fat) / 9), // 1g yağ = 9 kalori
      proteinPercent: Math.round(split.protein * 100),
      carbsPercent: Math.round(split.carbs * 100),
      fatPercent: Math.round(split.fat * 100),
    };

    setResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      goalCalories: Math.round(goalCalories),
      macros,
    });
    trackCalculatorComplete("calorie");
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-4xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
              <Calculator className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-bold text-4xl text-foreground leading-tight md:text-5xl lg:text-6xl">
            Kalori Hesaplayıcı
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Günlük kalori ihtiyacınızı (TDEE) hesaplayın ve hedeflerinize uygun
            beslenme planı oluşturun.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Hesaplayıcı Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-primary" />
                Bilgilerinizi Girin
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Cinsiyet</Label>
                <RadioGroup value={gender} onValueChange={setGender}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="male" id="male" />
                    <Label htmlFor="male">Erkek</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="female" id="female" />
                    <Label htmlFor="female">Kadın</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="age">Yaş</Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="25"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Kilo (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    placeholder="70"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="height">Boy (cm)</Label>
                <Input
                  id="height"
                  type="number"
                  placeholder="175"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Aktivite Seviyesi</Label>
                <Select value={activity} onValueChange={setActivity}>
                  <SelectTrigger>
                    <SelectValue placeholder="Aktivite seviyenizi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(activityLevels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Hedefiniz</Label>
                <Select value={goal} onValueChange={setGoal}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(goals).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value.name} - {value.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Makro Dağılım Tercihi</Label>
                <Select value={macroSplit} onValueChange={setMacroSplit}>
                  <SelectTrigger>
                    <SelectValue placeholder="Makro dağılımı seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(macroSplits).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        <div>
                          <div className="font-medium">{value.name}</div>
                          <div className="text-muted-foreground text-xs">
                            {value.detail}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {
                    macroSplits[macroSplit as keyof typeof macroSplits]
                      .description
                  }
                </p>
              </div>

              <Button
                onClick={calculateCalories}
                className="w-full"
                disabled={!age || !gender || !weight || !height || !activity}
              >
                Kalori Hesapla
              </Button>
            </CardContent>
          </Card>

          {/* Sonuçlar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Sonuçlarınız
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border border-border bg-accent/50 p-4">
                      <div className="mb-2 flex items-center gap-2">
                        <Heart className="h-4 w-4 text-primary" />
                        <span className="font-medium text-muted-foreground text-xs">
                          BMR
                        </span>
                      </div>
                      <div className="font-bold text-2xl text-foreground">
                        {results.bmr}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Bazal Metabolizma
                      </div>
                    </div>
                    <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
                      <div className="mb-2 flex items-center gap-2">
                        <Activity className="h-4 w-4 text-primary" />
                        <span className="font-medium text-muted-foreground text-xs">
                          TDEE
                        </span>
                      </div>
                      <div className="font-bold text-2xl text-primary">
                        {results.tdee}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Günlük İhtiyaç
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border/50 bg-card p-6">
                    <div className="text-center">
                      <div className="mb-2 flex items-center justify-center gap-2">
                        <Target className="h-5 w-5 text-primary" />
                        <span className="font-medium text-muted-foreground text-sm">
                          Hedef Kalori
                        </span>
                      </div>
                      <div className="font-bold text-3xl text-primary">
                        {results.goalCalories} kcal
                      </div>
                      <div className="text-muted-foreground text-sm">
                        {goals[goal as keyof typeof goals].name} İçin Günlük
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold">Önerilen Makro Dağılımı:</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-lg border border-border bg-accent/30 p-3 text-center">
                        <div className="font-bold text-foreground text-lg">
                          {results.macros.protein}g
                        </div>
                        <div className="mt-1 text-muted-foreground text-xs">
                          Protein
                        </div>
                        <div className="mt-0.5 font-medium text-primary text-xs">
                          {results.macros.proteinPercent}%
                        </div>
                      </div>
                      <div className="rounded-lg border border-border bg-accent/30 p-3 text-center">
                        <div className="font-bold text-foreground text-lg">
                          {results.macros.carbs}g
                        </div>
                        <div className="mt-1 text-muted-foreground text-xs">
                          Karbonhidrat
                        </div>
                        <div className="mt-0.5 font-medium text-primary text-xs">
                          {results.macros.carbsPercent}%
                        </div>
                      </div>
                      <div className="rounded-lg border border-border bg-accent/30 p-3 text-center">
                        <div className="font-bold text-foreground text-lg">
                          {results.macros.fat}g
                        </div>
                        <div className="mt-1 text-muted-foreground text-xs">
                          Yağ
                        </div>
                        <div className="mt-0.5 font-medium text-primary text-xs">
                          {results.macros.fatPercent}%
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-muted bg-muted/30 p-4">
                    <div className="flex items-start gap-3">
                      <Info className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                      <p className="text-muted-foreground text-sm">
                        <strong className="text-foreground">Not:</strong> Bu
                        hesaplama yaklaşık bir tahmindir. Bireysel farklılıklar
                        için uzman görüşü alınız.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  Hesaplama yapmak için sol taraftaki formu doldurun
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <CalculatorArticle content={calorieGuide} />
      </div>
    </div>
  );
}
